import { NativeSelect, VStack, Field } from '@chakra-ui/react';
import { useGeoUnits } from '../../../hooks/useGeoAnalytics'
import { US_STATES } from '../../../types/geoAnalytics'
import { OFFICE_ORDER, officeLabel, officeUnitType, parseDistrictCode, type GuidedListSpec, type OfficeKey } from '../../../types/guidedList'

interface StepProps { spec: GuidedListSpec; patch: (p: Partial<GuidedListSpec>) => void }

export function LocalityStep({ spec, patch }: StepProps) {
  const unitsQuery = useGeoUnits(spec.state ? { type: officeUnitType(spec.office), state: spec.state } : null)
  const districts = [...(unitsQuery.data?.features ?? [])]
    .map(f => ({ id: String(f.properties?.id ?? ''), name: String(f.properties?.name ?? '') }))
    .filter(o => o.id)
    .sort((a, b) => a.name.localeCompare(b.name))

  // The Select's option values are unit ids; the spec stores the parsed district code.
  // Map the stored code back to its id so the control stays controlled after a rail jump-back.
  const selectedId = districts.find(d => parseDistrictCode(d.name) === spec.districtCode)?.id ?? ''

  const onDistrict = (id: string) => {
    const name = districts.find(d => d.id === id)?.name ?? ''
    patch({ districtCode: parseDistrictCode(name) })
  }

  return (
    <VStack align="stretch" gap={4}>
      <Field.Root>
        <Field.Label fontSize="sm" fontWeight="semibold">State</Field.Label>
        <NativeSelect.Root>
          <NativeSelect.Field
            placeholder="Select a state"
            value={spec.state}
            onChange={e => patch({ state: e.target.value, districtCode: null })}>
            {US_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field.Root>

      <Field.Root>
        <Field.Label fontSize="sm" fontWeight="semibold">Office</Field.Label>
        <NativeSelect.Root>
          <NativeSelect.Field
            value={spec.office}
            onChange={e => patch({ office: e.target.value as OfficeKey, districtCode: null })}>
            {OFFICE_ORDER.map(k => <option key={k} value={k}>{officeLabel(k)}</option>)}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field.Root>

      <Field.Root disabled={!spec.state || unitsQuery.isLoading}>
        <Field.Label fontSize="sm" fontWeight="semibold">District</Field.Label>
        <NativeSelect.Root>
          <NativeSelect.Field
            placeholder={unitsQuery.isLoading ? 'Loading districts…' : 'Select a district'}
            value={selectedId}
            onChange={e => onDistrict(e.target.value)}>
            {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field.Root>
    </VStack>
  );
}
